import { useContext, useState } from "react";
import {
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListSubheader,
    TextField,
    Tooltip,
} from "@mui/material";
import OutsideClickHandler from "react-outside-click-handler";

import { Edit, Save } from "@mui/icons-material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import KeyIcon from "@mui/icons-material/Key";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NumbersIcon from "@mui/icons-material/Numbers";
import HomeIcon from "@mui/icons-material/Home";
import DomainIcon from "@mui/icons-material/Domain";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PublicIcon from "@mui/icons-material/Public";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";
import CommentIcon from "@mui/icons-material/Comment";

import api from "../../../api";
import useNotification from "../../Notification";
import { ScreenContext } from "..";

const EnterpriseField = ({ icon, field, label, placeholder }) => {

    const { profile, setProfile, enterpriseId } = useContext(ScreenContext);

    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(profile[field] || "");
    const [loading, setLoading] = useState(false);

    const fetchUpdate = async () => {
        setLoading(true);

        const res = await api.patch(
            `api/enterprises/${enterpriseId}/contact_enterprises/${profile.id}/`,
            {
                [field]: value,
            }
        );

        setLoading(false);

        if (res.status === 200) {
            setProfile(res.data);
            setEditing(false);

            useNotification(
                "Sucesso!",
                "Empresa alterada com sucesso.",
                "success"
            );
        }
    };

    return (
	    <ListItem className="contact-general-listitem">
	        <Box className="contact-general-item-box">
	            <span className="align-icons">
	                <Box sx={{ display: "flex" }}>{icon}</Box>
	                {!editing && <Box>{label}</Box>}
	            </span>
	        </Box>

	        {!editing ? (
	            <Box className="contact-general-field">
	                <Box
					    className={`contact-general-data ${
					        !profile[field] ? "placeholder" : ""
					    }`}
					>
					    {profile[field] || placeholder}
					</Box>

	                <IconButton
	                    sx={{ fontSize: ".9em", padding: ".1rem" }}
	                    onClick={() => setEditing(true)}
	                >
	                    <Edit />
	                </IconButton>
	            </Box>
	        ) : (
	            <Box>
	                <OutsideClickHandler
	                    onOutsideClick={() => {
	                        setValue(profile[field] || "");
	                        setEditing(false);
	                    }}
	                >
	                    <TextField
	                        autoFocus
	                        size="small"
	                        value={value}
	                        onChange={(e) => setValue(e.target.value)}
	                        InputProps={{
	                            endAdornment: (
	                                <InputAdornment position="end">
	                                    <IconButton
	                                        disabled={loading}
	                                        onClick={fetchUpdate}
	                                    >
	                                        {loading ? (
	                                            <CircularProgress size={20} />
	                                        ) : (
	                                            <Save />
	                                        )}
	                                    </IconButton>
	                                </InputAdornment>
	                            ),
	                        }}
	                    />
	                </OutsideClickHandler>
	            </Box>
	        )}
	    </ListItem>
	);
};

const EnterpriseProfile = () => {
    return (
    	<Box fontSize=".9rem">
	        <List disablePadding>
	            <ListSubheader
			        disableSticky
			        sx={{
			            bgcolor: "transparent",
			            fontSize: ".8rem",
			            fontWeight: 600,
			            textTransform: "uppercase",
			            color: "text.secondary",
			        }}
			    >
			        Contato
			    </ListSubheader>

	            <EnterpriseField
	                icon={<AssignmentIndIcon />}
	                field="cnpj"
	                label="CNPJ"
	                placeholder="CNPJ"
	            />

	            <EnterpriseField
	                icon={<BusinessOutlinedIcon />}
	                field="name"
	                label="Nome"
	                placeholder="Nome"
	            />

	            <EnterpriseField
	                icon={<ApartmentOutlinedIcon />}
	                field="social_reason"
	                label="Razão Social"
	                placeholder="Razão Social"
	            />

	            <EnterpriseField
	                icon={<CallOutlinedIcon />}
	                field="telephone"
	                label="Telefone"
	                placeholder="Telefone"
	            />

	            <EnterpriseField
	                icon={<EmailOutlinedIcon />}
	                field="email"
	                label="E-mail"
	                placeholder="E-mail"
	            />

	            <EnterpriseField
	                icon={<LinkedInIcon />}
	                field="linkedin_url"
	                label="LinkedIn"
	                placeholder="LinkedIn"
	            />

	            <EnterpriseField
	                icon={<EmailOutlinedIcon />}
	                field="domain"
	                label="Domínio"
	                placeholder="Domínio"
	            />

	            <ListSubheader
			        disableSticky
			        sx={{
			            bgcolor: "transparent",
			            fontSize: ".8rem",
			            fontWeight: 600,
			            textTransform: "uppercase",
			            color: "text.secondary",
			        }}
			    >
			        Área de Atuação
			    </ListSubheader>

	            <EnterpriseField
	                icon={<KeyIcon />}
	                field="code"
	                label="Código"
	                placeholder="Código"
	            />

	            <EnterpriseField
	                icon={<WorkOutlineIcon />}
	                field="field"
	                label="Campo"
	                placeholder="Campo"
	            />

	            <ListSubheader
			        disableSticky
			        sx={{
			            bgcolor: "transparent",
			            fontSize: ".8rem",
			            fontWeight: 600,
			            textTransform: "uppercase",
			            color: "text.secondary",
			        }}
			    >
			        Endereço
			    </ListSubheader>

	            <EnterpriseField
	                icon={<LocationOnIcon />}
	                field="address"
	                label="Endereço"
	                placeholder="Endereço"
	            />

	            <EnterpriseField
	                icon={<NumbersIcon />}
	                field="address_number"
	                label="Número"
	                placeholder="Número"
	            />

	            <EnterpriseField
	                icon={<HomeIcon />}
	                field="address_extra"
	                label="Complemento"
	                placeholder="Complemento"
	            />

	            <EnterpriseField
	                icon={<DomainIcon />}
	                field="neighborhood"
	                label="Bairro"
	                placeholder="Bairro"
	            />

	            <EnterpriseField
	                icon={<LocationCityIcon />}
	                field="city"
	                label="Cidade"
	                placeholder="Cidade"
	            />

	            <EnterpriseField
	                icon={<PublicIcon />}
	                field="estate"
	                label="Estado"
	                placeholder="Estado"
	            />

	            <EnterpriseField
	                icon={<MarkunreadMailboxIcon />}
	                field="postal_code"
	                label="CEP"
	                placeholder="CEP"
	            />

	            <EnterpriseField
	                icon={<CommentIcon />}
	                field="comments"
	                label="Comentários"
	                placeholder="Comentários"
	            />
	        </List>
	    </Box>
    );
};

export default EnterpriseProfile;
